/**
 * Public page where customers redeem a self-service license link.
 */
import React, { useEffect, useState } from 'react';
import {
  Card, Form, Input, Select, Button, Result, Spin, Row, Col, Typography, message,
} from 'antd';
import { useParams } from 'react-router-dom';
import { selfServiceApi } from './api';
import type { MachineInput } from './types';
import { DB_VENDORS, PRODUCT_VERSIONS } from './constants';

const { Title, Text } = Typography;

type PageState = 'loading' | 'valid' | 'expired' | 'not_found' | 'success' | 'error';

const SelfServiceLicensePage: React.FC = () => {
  const { linkId } = useParams<{ linkId: string }>();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [form] = Form.useForm<MachineInput>();
  const [submitting, setSubmitting] = useState(false);
  const [resultEmail, setResultEmail] = useState('');

  useEffect(() => {
    if (!linkId) { setPageState('not_found'); return; }
    selfServiceApi.check(linkId).then((res) => {
      setPageState(res.valid ? 'valid' : 'expired');
    }).catch(() => setPageState('not_found'));
  }, [linkId]);

  const handleSubmit = async () => {
    if (!linkId) return;
    setSubmitting(true);
    try {
      const values = await form.validateFields();
      const submitData: MachineInput = {
        ...values,
        supported_databases: Array.isArray(values.supported_databases)
          ? values.supported_databases.join(',')
          : values.supported_databases,
      };
      const result = await selfServiceApi.redeem(linkId, submitData);
      setResultEmail(values.customer_contact_email);
      setPageState('success');
      if (!result.email_sent) {
        message.warning('License was generated but the email could not be sent. Please contact support@gudusoft.com.');
      }
    } catch (error: any) {
      if (error.message?.includes('already been used')) {
        setPageState('expired');
      } else {
        message.error(error.message || 'Something went wrong');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const cardStyle: React.CSSProperties = {
    maxWidth: 720,
    margin: '40px auto',
  };

  if (pageState === 'loading') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 120 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (pageState === 'not_found') {
    return (
      <Card style={cardStyle}>
        <Result status="404" title="Link Not Found" subTitle="This link does not exist." />
      </Card>
    );
  }

  if (pageState === 'expired') {
    return (
      <Card style={cardStyle}>
        <Result
          status="warning"
          title="Link Expired"
          subTitle={
            <span>
              This link has already been used. If you need a new license, please contact{' '}
              <a href="mailto:support@gudusoft.com">support@gudusoft.com</a>.
            </span>
          }
        />
      </Card>
    );
  }

  if (pageState === 'success') {
    return (
      <Card style={cardStyle}>
        <Result
          status="success"
          title="License Sent!"
          subTitle={`Your license has been sent to ${resultEmail}. Please check your inbox (and spam folder).`}
        />
      </Card>
    );
  }

  // pageState === 'valid' — show the form
  return (
    <Card
      style={cardStyle}
      title={
        <div style={{ textAlign: 'center' }}>
          <Title level={3} style={{ margin: 0 }}>Generate Your SQLFlow License</Title>
          <Text type="secondary">Fill in the details below to receive your license by email.</Text>
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          product_name: 'Gudu SQLFlow',
          product_license: 'Site',
          product_version: PRODUCT_VERSIONS[0],
          license_type: 'machine_code',
          purchase_date: new Date().toISOString().split('T')[0],
          available_days: 30,
          max_user: 50,
        }}
      >
        {/* User-editable fields first */}
        <Form.Item
          name="machine_code"
          label="Machine Code"
          rules={[{ required: true, message: 'Please enter machine code' }]}
        >
          <Input.TextArea rows={3} placeholder="Enter machine code" />
        </Form.Item>

        <Form.Item
          name="supported_databases"
          label="Supported Databases (max 3)"
          rules={[
            { required: true, message: 'Please select supported databases' },
            {
              validator: (_, value) =>
                value && value.length > 3
                  ? Promise.reject('You can select up to 3 databases')
                  : Promise.resolve(),
            },
          ]}
        >
          <Select mode="multiple" placeholder="Select up to 3 databases" maxTagCount={3}>
            {DB_VENDORS.map(db => (
              <Select.Option key={db.value} value={db.value}>{db.label}</Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="customer_company_name"
              label="Company Name"
              rules={[{ required: true, message: 'Please enter company name' }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="customer_contact_name"
              label="Contact Name"
              rules={[{ required: true, message: 'Please enter contact name' }]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="customer_contact_email"
          label="Email Address"
          rules={[
            { required: true, message: 'Please enter your email' },
            { type: 'email', message: 'Please enter a valid email' },
          ]}
        >
          <Input placeholder="you@company.com" />
        </Form.Item>

        {/* Fixed hidden fields */}
        <Form.Item name="product_name" hidden><Input /></Form.Item>
        <Form.Item name="product_license" hidden><Input /></Form.Item>
        <Form.Item name="product_version" hidden><Input /></Form.Item>
        <Form.Item name="license_type" hidden><Input /></Form.Item>
        <Form.Item name="purchase_date" hidden><Input /></Form.Item>
        <Form.Item name="available_days" hidden><Input /></Form.Item>
        <Form.Item name="max_user" hidden><Input /></Form.Item>

        <Form.Item style={{ textAlign: 'center', marginBottom: 0 }}>
          <Button type="primary" size="large" loading={submitting} onClick={handleSubmit}>
            Generate & Send License
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default SelfServiceLicensePage;
